import { useCallback, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  AiStarFilled,
  Download,
  StatusChange,
  TrashCan,
} from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';

import { useRowActionsContext } from '../../../../hooks/knowledge-base/useKnowledgeBaseAccess.js';
import type { RagTableType } from '../../../../types/rag.js';
import { ActionMenu, ActionMenuItem } from '../../ActionMenu.jsx';

type Props = {
  onOpenDeleteDialog: (document: RagTableType[]) => void;
  onUnselectTableRows: () => void;
  data?: RagTableType;
  className?: string;
  onOpenMoveDialog?: () => void;
};

const ActionsCell = ({
  data,
  onOpenDeleteDialog,
  onUnselectTableRows,
  onOpenMoveDialog,
  className,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  // The page decides the row actions once; a cell only looks them up.
  const { isFileSystemEnabled, getActions, download } = useRowActionsContext();
  const [open, setOpen] = useState(false);

  const actions = data ? getActions(data) : [];

  const showDownload = !isFileSystemEnabled || actions.includes('download');
  const showDelete = !isFileSystemEnabled || actions.includes('delete');

  const handleDelete = useCallback(() => {
    if (!data) return;
    onOpenDeleteDialog([data]);
    setOpen(false);
  }, [data, onOpenDeleteDialog]);

  const handleDownload = () => {
    if (!data) return;

    download(data).catch(() => {
      enqueueSnackbar({
        variant: 'error',
        subTitle: t('The file could not be downloaded'),
        removable: true,
      });
    });
    onUnselectTableRows();
    setOpen(false);
  };

  if (!actions.length) return;

  return (
    <ActionMenu
      open={open}
      onOpenChange={setOpen}
      aria-label="actions-button"
      className={className}
    >
      {isFileSystemEnabled && actions.includes('ask') && (
        <ActionMenuItem
          // onClick={askMintIq}
          aria-label="ask-mintiq-file"
          startIcon={<AiStarFilled className="text-text-primary" />}
          label={t('Ask MintIQ')}
        />
      )}
      {isFileSystemEnabled && actions.includes('move') && (
        <ActionMenuItem
          onClick={onOpenMoveDialog}
          startIcon={<StatusChange className="fill-text-primary" />}
          label={t('Move to')}
        />
      )}
      {showDownload && (
        <ActionMenuItem
          aria-label="download-file"
          onClick={() => handleDownload()}
          disabled={!actions.includes('download')}
          startIcon={<Download className="fill-text-primary" />}
          label={t('Download')}
        />
      )}
      {showDelete && (
        <ActionMenuItem
          icon
          startIcon={<TrashCan className="fill-error-dark" />}
          label={t('Delete')}
          tone="danger"
          aria-label="delete-file"
          onClick={() => handleDelete()}
          disabled={!actions.includes('delete')}
        />
      )}
    </ActionMenu>
  );
};

export { ActionsCell };
